import React, { useMemo, useState } from "react";
import { TokenUSDC } from '@web3icons/react'

// Reactive pricing cards for the given plan table (JavaScript only)
// - Dark theme with cyan & purple accents
// - Mobile-first, responsive grid for desktop
// - Monthly / Annual billing toggle
// - Accessible, keyboard-friendly controls
// - Lightweight (pure Tailwind) single-file component

// Tailwind color hints used here (adjust in your config if needed):
// cyan: from tailwind palette (e.g., cyan-400/500)
// purple: from tailwind palette (e.g., purple-500/600)
// background relies on dark mode; ensure your app/container has `className="dark"` or uses a dark theme provider

const PLANS = [
  {
    key: "Free",
    monthly: "$0",
    annual: "$0",
    apiMonthlyMetrics: "1k",
    oracleFeeds: "N/A",
    mcpConnectors: "N/A",
    support: "Community only",
    cta: "Included",
    available: true,
    description: "Great for trying out the API and building small projects.",
  },
  {
    key: "Metrics Starter",
    monthly: "$19",
    annual: "$199",
    apiMonthlyMetrics: "100k",
    oracleFeeds: "N/A",
    mcpConnectors: "3 (API limits apply)",
    support: "Email / forum",
    cta: "Choose Metrics Starter",
    available: false,
    description: "Ideal for developers and small teams building apps with moderate API usage.",
  },
  {
    key: "Metrics Growth",
    monthly: "$99",
    annual: "$999",
    apiMonthlyMetrics: "1M",
    oracleFeeds: "N/A",
    mcpConnectors: "10 (API limits apply)",
    support: "Priority SLA",
    badge: "Popular",
    cta: "Choose Metrics Growth",
    highlight: false,
    available: false,
    description: "Perfect for growing teams and businesses with higher API demands.",
  },
  {
    key: "Oracle Pro",
    monthly: "$19",
    annual: "$199",
    apiMonthlyMetrics: "N/A",
    oracleFeeds: "Unlimited",
    mcpConnectors: "N/A",
    support: "Email / forum",
    cta: "Choose Oracle Pro",
    available: false,
    description: "Smart Contract access for Web3 EVM. No metrics access. Available add on for all metrics based plans.",
  },
];

const formatPrice = (plan, cycle) => (cycle === "monthly" ? plan.monthly : plan.annual);

const gradientRing = "bg-[conic-gradient(at_top_right,_theme(colors.cyan.500),theme(colors.purple.500),theme(colors.cyan.500))]";

const Check = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="h-5 w-5"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.943a.75.75 0 0 0-1.22-.874l-3.284 4.583-1.58-1.58a.75.75 0 1 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.754-5.345Z"
      clipRule="evenodd"
    />
  </svg>
);

const ToggleSwitch = ({ value, onChange }) => {
  const isAnnual = value === "annual";
  return (
    <div className="inline-flex items-center gap-3">
      <span className="text-sm text-gray-300">Monthly</span>
      <button
        type="button"
        role="switch"
        aria-checked={isAnnual}
        onClick={() => onChange(isAnnual ? "monthly" : "annual")}
        className={[
          "relative h-7 w-14 rounded-full transition-colors duration-300",
          isAnnual ? "bg-purple-600/70" : "bg-cyan-600/70",
          "ring-1 ring-inset ring-white/10",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60",
        ].join(" ")}
      >
        <span
          className={[
            "absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white/90 shadow",
            "transition-transform duration-300",
            isAnnual ? "translate-x-7" : "translate-x-0",
          ].join(" ")}
        />
      </button>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-300">Annual</span>
        <span className="text-xs rounded-md px-2 py-0.5 bg-purple-500/10 text-purple-300 border border-purple-500/30">
          save ~15%
        </span>
      </div>
    </div>
  );
};

const PlanCard = ({ plan, billing, onSelect, available }) => {
  const price = useMemo(() => formatPrice(plan, billing), [plan, billing]);

  return (
    <div
      className={[
        "group relative rounded-2xl p-[3px]",
        plan.highlight ? gradientRing : "bg-white/5",
        "shadow/40 shadow-black/30",
      ].join(" ")}
    >
      <div className="rounded-2xl h-full w-full bg-gray-900/95 p-6 sm:p-7 flex flex-col">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-2xl font-semibold text-cyan-300">{plan.key}</h3>
          {plan.badge && (
            <span className="text-xs px-2 py-1 rounded-md border border-cyan-400/40 text-cyan-300 bg-cyan-400/10">
              {plan.badge}
            </span>
          )}
        </div>

        <div className="mb-5">
          <div className="flex flex-row text-3xl font-bold tracking-tight text-white items-center justify-center">
            {price} <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" viewBox="0 0 24 24">
              <path fill="#0B53BF" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18"/>
              <path fill="#fff" d="M13.62 5.45v1.159a5.64 5.64 0 0 1 4.005 5.394 5.64 5.64 0 0 1-4.005 5.394v1.16a6.74 6.74 0 0 0 5.13-6.554 6.74 6.74 0 0 0-5.13-6.553m-7.245 6.553a5.64 5.64 0 0 1 4.005-5.394V5.45a6.74 6.74 0 0 0-5.13 6.553 6.74 6.74 0 0 0 5.13 6.553v-1.159a5.63 5.63 0 0 1-4.005-5.394"/>
              <path fill="#fff" d="M14.419 13.258c0-2.301-3.606-1.356-3.606-2.627 0-.456.366-.748 1.063-.748.833 0 1.12.405 1.21.95h1.147c-.102-1.024-.69-1.67-1.67-1.863v-.904h-1.125v.872c-1.075.137-1.75.762-1.75 1.693 0 2.312 3.611 1.445 3.611 2.694 0 .472-.455.787-1.226.787-1.007 0-1.339-.444-1.462-1.057H9.49c.073 1.122.764 1.823 1.947 1.999v.886h1.125v-.875c1.153-.149 1.856-.82 1.856-1.807"/>
          </svg>
          </div>
          <div className="mt-1 text-xs text-gray-400">
            {billing === "monthly" ? "per month" : "per year"}
          </div>
        </div>
        <div>
          {plan.description && (
            <p className="mb-4 text-sm text-gray-300">
              {plan.description}
            </p>
          )}
        </div>

        <ul className="mb-6 space-y-3 text-sm text-gray-200 list-disc list-inside pl-2">
          <li className="flex flex-row items-center gap-2 text-left">
            <span className="text-cyan-400"><Check /></span>
            <span className="flex-1">
              <span className="font-medium text-white">Monthly Metrics Returned:</span>
              <span className="ml-2 text-cyan-200">{plan.apiMonthlyMetrics}</span>
            </span>
          </li>
          <li className="flex flex-row items-center gap-2 text-left">
            <span className="text-cyan-400"><Check /></span>
            <span className="flex-1">
              <span className="font-medium text-white">Oracle Feeds:</span>
              <span className="ml-2 text-cyan-200">{plan.oracleFeeds}</span>
            </span>
          </li>
          <li className="flex flex-row items-center gap-2 text-left">
            <span className="text-cyan-400"><Check /></span>
            <span className="flex-1">
              <span className="font-medium text-white">MCP Connectors:</span>
              <span className="ml-2 text-cyan-200">{plan.mcpConnectors}</span>
            </span>
          </li>
          <li className="flex flex-row items-center gap-2 text-left">
            <span className="text-cyan-400"><Check /></span>
            <span className="flex-1">
              <span className="font-medium text-white">Support:</span>
              <span className="ml-2 text-cyan-200">{plan.support}</span>
            </span>
          </li>
        </ul>

        {available ? <div className="mt-auto">
          <button
            onClick={() => onSelect && onSelect(plan.key, billing)}
            className={[
              "w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition",
              plan.highlight
                ? "bg-gradient-to-r from-cyan-500 to-purple-600 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.15)_inset]"
                : "bg-white/10 text-white hover:bg-white/15",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60",
            ].join(" ")}
          >
            {plan.cta || "Select Plan"}
          </button>
        </div>:<>Coming Soon</>}
      </div>
    </div>
  );
};

const PricingComparisonRow = ({ label, values }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-center text-sm">
    <div className="text-gray-300">{label}</div>
    {values.map((v, i) => (
      <div
        key={i}
        className="rounded-lg bg-white/5 px-3 py-2 text-white text-center border border-white/5"
      >
        {v}
      </div>
    ))}
  </div>
);

const LegendDot = ({ className }) => (
  <span className={"inline-block h-2 w-2 rounded-full " + (className || "bg-cyan-400")} />
);

const HeaderAccent = () => (
  <div className="pointer-events-none absolute inset-x-0 -top-24 h-48 opacity-60 blur-3xl">
    <div className="mx-auto h-full w-3/4 rounded-full bg-gradient-to-r from-cyan-500/30 via-purple-500/30 to-cyan-500/30" />
  </div>
);

const PricingSectionHeader = ({ billing, setBilling }) => (
  <div className="relative mb-8 sm:mb-10">
    <HeaderAccent />
    <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">Plans built for every stage</h2>
        <p className="mt-2 text-sm text-gray-300">
          REST, Oracles, and MCP access with predictable limits and simple scaling.
        </p>
      </div>
      <ToggleSwitch value={billing} onChange={setBilling} />
    </div>
  </div>
);

export default function SubscriptionPricingCards({ className, onSelect }) {
  const [billing, setBilling] = useState("monthly");

  return (
    <section className={[
      "w-full", className || "",
      "bg-gradient-to-b bg-slate-700",
      "text-gray-200 px-2 sm:px-6 md:px-8 py-2 sm:py-2",
      "rounded-2xl",
    ].join(" ")}
    >
      <PricingSectionHeader billing={billing} setBilling={setBilling} />

      {/* Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6">
        {PLANS.map((plan) => (
          <PlanCard key={plan.key} plan={plan} billing={billing} onSelect={onSelect} available={plan.available} />
        ))}
      </div>

      {/* Compact comparison (mobile/tablet friendly) */}
      <div className="mt-10 sm:mt-12 space-y-3 mb-3">
    
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <LegendDot className="bg-cyan-400 min-w-2" />
          <span className="text-left">All prices in {billing === "monthly" ? "monthly" : "annual"} terms and are subject to change.</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <LegendDot className="bg-cyan-400 min-w-2" />
          <span className="text-left">Subscriptions for Oracle Pro are available in addition to all other plans and are managed by Smart Contracts on chain and paid for with you wallet.</span>
        </div>          
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <LegendDot className="bg-cyan-400 min-w-2" />
          <span className="text-left">NOTICE: All limits are applied automatically so users should monitor their usage closely. See our documentation for more details on how to monitor usage via our API.</span>
        </div>
        
        {/* <div className="space-y-3">
          <PricingComparisonRow label="API Calls" values={PLANS.map(p => p.apiMonthlyMetrics)} />
          <PricingComparisonRow label="Oracle Feeds" values={PLANS.map(p => p.oracleFeeds)} />
          <PricingComparisonRow label="MCP Connectors" values={PLANS.map(p => p.mcpConnectors)} />
          <PricingComparisonRow label="Support" values={PLANS.map(p => p.support)} />
        </div> */}
      </div>

      {/* Footer note */}
      {/* <div className="mt-8 text-xs text-gray-400">
        * Enterprise pricing is customized. Contact us for dedicated SLAs, private feeds, and volume terms.
      </div> */}
    </section>
  );
}
